// react
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

// radix
import * as RadixRadioGroup from '@radix-ui/react-radio-group';

// components
import {
  BaseField,
  FieldProps,
  Memphis,
  MemphisVariant,
  Option,
} from '@/components/atoms';
import { CardContainer } from '@/components/templates';

/**
 * RadioGroup allows the user to select a boolean value.
 */
export const RadioGroup = ({
  id,
  label,
  description,
  tooltip,
  value,
  onChange,
  isRequired = false,
  isHideTitle = false,
  ariaLabel,
  options,
  isDisabled = false,
  maxColumns = 2,
  variant = 'outlined',
}: RadioGroupProps) => {
  const sortedOptions = useMemo(() => {
    return options?.sort((a, b) => (a?.order || 0) - (b?.order || 0));
  }, [options]);

  return (
    <BaseField
      id={id}
      label={label}
      ariaLabel={ariaLabel}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <RadioGroupRoot
        value={value}
        onValueChange={onChange}
        aria-label={ariaLabel}
        aria-labelledby={id}
        disabled={isDisabled}
      >
        <CardContainer maxColumns={maxColumns} isPreventHeightMatch size='sm'>
          {sortedOptions?.map((option) => (
            <RadioGroupItem
              key={option.value}
              value={option.value}
              id={option.value}
              $isDisabled={isDisabled}
              $variant={variant}
              $isSelected={value === option.value}
            >
              <RadioWrapper variant={variant}>
                {variant !== 'box-fill' && (
                  <RadioGroupSelector $isDisabled={isDisabled}>
                    <RadioGroupIndicator $isDisabled={isDisabled} />
                  </RadioGroupSelector>
                )}
                <Label htmlFor={option.value} $isDisabled={isDisabled}>
                  {option.label}
                </Label>
              </RadioWrapper>
              {option.memphis && <Memphis size='sm' variant={option.memphis} />}
            </RadioGroupItem>
          ))}
        </CardContainer>
      </RadioGroupRoot>
    </BaseField>
  );
};

const RadioGroupRoot = styled(RadixRadioGroup.Root)`
  display: flex;
  gap: ${(props) => props.theme.spacing[16]};
`;

interface RadioGroupItemProps {
  $isDisabled: boolean;
  $variant: RadioGroupVariant;
  $isSelected: boolean;
}

const getVariantStyles = (variant: RadioGroupVariant, isSelected: boolean) => {
  switch (variant) {
    case 'default':
      return css`
        background: none;
        border: none;
        padding: 0;

        &:focus {
          outline: none;
        }
      `;
    case 'outlined':
      return css`
        background: ${(props) => props.theme.color.neutral[10]};
        border: 1px solid ${(props) => props.theme.color.neutral[60]};
        border-radius: 8px;
        padding: 0;
        padding-right: ${(props) => props.theme.spacing[16]};
        justify-content: space-between;
        &:focus {
          outline: 1px solid ${(props) => props.theme.color.primary[60]};
        }
      `;
    case 'box-fill':
      return css`
        background: ${(props) => props.theme.color.neutral[10]};
        border: 1px solid ${(props) => props.theme.color.neutral[60]};
        border-radius: 8px;
        padding: 0;
        justify-content: space-between;
        ${(props) =>
          isSelected &&
          css`
            background: ${props.theme.color.cards.red.primary};
          `}
        &:focus {
          outline: none;
        }
      `;
  }
};

const getVariantPadding = (variant: RadioGroupVariant) => {
  switch (variant) {
    case 'default':
      return css`
        padding: 0;
      `;
    case 'outlined':
      return css`
        padding: ${(props) => props.theme.spacing[24]}
          ${(props) => props.theme.spacing[16]};
        padding-right: 0;
      `;
    case 'box-fill':
      return css`
        padding: ${(props) => props.theme.spacing[24]};
      `;
  }
};

const RadioGroupItem = styled(RadixRadioGroup.Item)<RadioGroupItemProps>`
  display: flex;
  align-items: center;
  text-align: left;
  gap: ${(props) => props.theme.spacing[16]};
  width: 100%;

  ${(props) => getVariantStyles(props.$variant, props.$isSelected)};

  cursor: pointer;

  ${(props) =>
    props.$isDisabled &&
    css`
      background: ${props.theme.color.neutral[30]};
      color: ${props.theme.color.neutral[50]};
      border: 1px solid ${props.theme.color.neutral[50]};
      cursor: default;
    `}

  transition-duration: 0.1s;
`;

const RadioWrapper = styled.div<{ variant: RadioGroupVariant }>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
  ${(props) => getVariantPadding(props.variant)};
`;

interface RadioGroupSelectorProps {
  $isDisabled: boolean;
}

const RadioGroupSelector = styled.div<RadioGroupSelectorProps>`
  background: ${(props) => props.theme.color.neutral[10]};

  width: 20px;
  min-width: 20px;
  max-width: 20px;
  height: 20px;
  min-height: 20px;
  max-height: 20px;
  border-radius: 99999px;
  border: 1px solid ${(props) => props.theme.color.neutral[60]};

  ${(props) =>
    props.$isDisabled &&
    css`
      background: ${props.theme.color.neutral[40]};
    `}
`;

interface RadioGroupIndicatorProps {
  $isDisabled: boolean;
}

const RadioGroupIndicator = styled(
  RadixRadioGroup.Indicator,
)<RadioGroupIndicatorProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;

  &::after {
    content: '';
    display: block;
    min-width: 10px;
    max-width: 10px;
    min-height: 10px;
    max-height: 10px;
    border-radius: 99999px;
    background: ${(props) => props.theme.color.primary[30]};
  }

  ${(props) =>
    props.$isDisabled &&
    css`
      &::after {
        background: ${props.theme.color.neutral[60]};
      }
    `}
`;

interface LabelProps {
  $isDisabled: boolean;
}

const Label = styled.label<LabelProps>`
  font: ${(props) => props.theme.typography.body.lg.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  cursor: pointer;
  text-align: left;

  ${(props) =>
    props.$isDisabled &&
    css`
      color: ${props.theme.color.neutral[60]};
      cursor: default;
    `}
`;

export type RadioGroupVariant = 'default' | 'outlined' | 'box-fill';

export interface RadioGroupOption extends Option {
  memphis?: MemphisVariant;
}

interface RadioGroupProps extends FieldProps<string> {
  options?: RadioGroupOption[];
  variant?: RadioGroupVariant;
  maxColumns?: number;
}

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
